import {
  PLATFORM_ACTIONS,
  PLATFORM_ROLES,
  type PlatformAction,
  type PlatformRole,
} from '@src/models/interfaces/platformAccount.interface';

/** Custom claim holding the platform staff role (short: claims share an 850-byte budget). */
export const PLATFORM_CLAIM_KEY = 'pf';

/** Staff sign in with Google on a company domain; the backend rejects anything else. */
export const PLATFORM_STAFF_SIGN_IN_PROVIDER = 'google.com';
export const PLATFORM_STAFF_EMAIL_DOMAINS = ['guarnic.com', 'operonda.com', 'teklion.com'];

/** Mirrors `PLATFORM_ACTION_ROLES` in services. `admin` may run every action. */
export const PLATFORM_ACTION_ROLES: Record<PlatformAction, PlatformRole[]> = {
  [PLATFORM_ACTIONS.EXTEND_TRIAL]: [PLATFORM_ROLES.ADMIN, PLATFORM_ROLES.SUPPORT],
  [PLATFORM_ACTIONS.CHANGE_PLAN]: [PLATFORM_ROLES.ADMIN, PLATFORM_ROLES.SALES],
  [PLATFORM_ACTIONS.MARK_PAID]: [PLATFORM_ROLES.ADMIN, PLATFORM_ROLES.FINANCE],
  [PLATFORM_ACTIONS.SUSPEND]: [PLATFORM_ROLES.ADMIN, PLATFORM_ROLES.FINANCE],
  [PLATFORM_ACTIONS.REACTIVATE]: [PLATFORM_ROLES.ADMIN, PLATFORM_ROLES.FINANCE],
  [PLATFORM_ACTIONS.CANCEL]: [PLATFORM_ROLES.ADMIN, PLATFORM_ROLES.FINANCE],
  [PLATFORM_ACTIONS.LINK_ORGANIZATION]: [PLATFORM_ROLES.ADMIN],
  [PLATFORM_ACTIONS.CORPORATION_UPSERT]: [PLATFORM_ROLES.ADMIN],
};

export interface PlatformClaims {
  [PLATFORM_CLAIM_KEY]?: unknown;
  email?: unknown;
  email_verified?: unknown;
  firebase?: { sign_in_provider?: unknown };
}

const isPlatformRole = (value: unknown): value is PlatformRole =>
  Object.values(PLATFORM_ROLES).includes(value as PlatformRole);

/** The email's domain, read with the URL parser rather than by splitting the string. */
const emailDomain = (email: string): string => {
  try {
    return new URL(`mailto://${email}`).hostname.toLowerCase();
  } catch {
    return '';
  }
};

/**
 * The caller's platform role, or `null` when the token has none or the session does not meet
 * the staff identity rules (Google sign-in, verified company email). Mirrors
 * `assertPlatformRole` in services so the UI never offers what the API will reject; the real
 * check stays on the backend.
 */
export const resolvePlatformRole = (
  claims: PlatformClaims | null | undefined,
): PlatformRole | null => {
  if (!claims) return null;
  const role = claims[PLATFORM_CLAIM_KEY];
  if (!isPlatformRole(role)) return null;
  if (claims.firebase?.sign_in_provider !== PLATFORM_STAFF_SIGN_IN_PROVIDER) return null;
  if (claims.email_verified !== true || typeof claims.email !== 'string') return null;
  return PLATFORM_STAFF_EMAIL_DOMAINS.includes(emailDomain(claims.email)) ? role : null;
};

export const canRunPlatformAction = (
  role: PlatformRole | null | undefined,
  action: PlatformAction,
): boolean =>
  !!role && (role === PLATFORM_ROLES.ADMIN || PLATFORM_ACTION_ROLES[action].includes(role));
